import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { DocumentsCard } from "../components/DocumentsCard";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Building2,
  CreditCard,
  Landmark,
  DollarSign,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/employee`);
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "badge-pending",
      processing: "bg-blue-100 text-blue-800 border border-blue-200",
      completed: "badge-completed",
      failed: "badge-failed",
      returned: "bg-orange-100 text-orange-800 border border-orange-200",
    };
    return variants[status] || "bg-stone-100 text-stone-800";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid lg:grid-cols-3 gap-6">
            <Skeleton className="h-48 lg:col-span-2" />
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="employee-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome, {user?.full_name?.split(" ")[0]}
            </h1>
            <p className="text-stone-600 mt-1">View your direct deposits and bank verification status.</p>
          </div>
          {stats?.bank_account_linked && (
            <Link to="/payments">
              <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="view-deposits-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                View Deposits
              </Button>
            </Link>
          )}
        </div>

        {/* Alert: No Bank Account */}
        {!stats?.bank_account_linked && (
          <Card className="border-amber-200 bg-amber-50" data-testid="no-bank-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Landmark className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">Link your bank account</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Connect your bank via Plaid so CPC can send your direct deposits securely.
                  </p>
                  <Link to="/bank-account">
                    <Button size="sm" className="mt-3 bg-amber-600 hover:bg-amber-700 rounded-full">
                      Link Bank Account
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alert: No Project Assigned */}
        {!stats?.current_project && (
          <Card className="border-blue-200 bg-blue-50" data-testid="no-project-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Awaiting project assignment</p>
                  <p className="text-sm text-blue-700 mt-1">
                    CPC's Account Manager will assign you to a project once your bank verification is complete.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Current Project */}
          {stats?.current_project ? (
            <Card className="lg:col-span-2" data-testid="project-card">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Your Project</CardTitle>
                <CardDescription>Project you're currently assigned to</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-8 h-8 text-emerald-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-stone-900">{stats.current_project.name}</h3>
                    <p className="text-stone-600 mt-1">{stats.current_project.address}</p>
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="p-3 bg-stone-50 rounded-lg">
                        <p className="text-sm text-stone-500">Project Code</p>
                        <p className="text-lg font-bold text-emerald-700">
                          {stats.current_project.name?.slice(0, 8).toUpperCase() || "—"}
                        </p>
                      </div>
                      <div className="p-3 bg-stone-50 rounded-lg">
                        <p className="text-sm text-stone-500">Assignment Status</p>
                        <p className="text-lg font-bold text-emerald-700">Active</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="lg:col-span-2">
              <CardContent className="p-6">
                <div className="text-center py-8">
                  <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">No Project Assigned</h3>
                  <p className="text-stone-600 max-w-sm mx-auto">
                    Once CPC's Account Manager assigns you to a project, you'll see the details here.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Direct Deposit Status */}
          <Card data-testid="deposit-status-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Direct Deposit</CardTitle>
              <CardDescription>Your deposit setup</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.bank_account_linked ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-stone-900">Bank linked</p>
                      <p className="text-sm text-stone-500">Ready to receive deposits from CPC</p>
                    </div>
                  </div>
                  <Link to="/bank-account">
                    <Button variant="outline" className="w-full rounded-full" data-testid="manage-bank-btn">
                      Manage Bank
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Landmark className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="font-medium text-stone-900">No bank linked</p>
                  <p className="text-sm text-stone-500 mt-1 mb-3">Link a bank to receive your direct deposits</p>
                  <Link to="/bank-account">
                    <Button size="sm" className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="link-bank-cta">
                      Link Bank Account
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid sm:grid-cols-2 gap-4">
          <Link to="/payments" className="block">
            <Card className="card-hover h-full cursor-pointer" data-testid="action-deposits">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-emerald-700" />
                </div>
                <div>
                  <h3 className="font-medium text-stone-900">Direct Deposits</h3>
                  <p className="text-sm text-stone-500">View deposit history</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/bank-account" className="block">
            <Card className="card-hover h-full cursor-pointer" data-testid="action-bank">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Landmark className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-medium text-stone-900">Bank Account</h3>
                  <p className="text-sm text-stone-500">
                    {stats?.bank_account_linked ? "Manage account" : "Link account"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Deposit History */}
        <Card data-testid="payment-history-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Deposit History</CardTitle>
              <CardDescription>Your recent direct deposits</CardDescription>
            </div>
            <Link to="/payments">
              <Button variant="ghost" size="sm" className="text-emerald-700">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.payment_history?.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-600">No deposits yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats?.payment_history?.map((payment, index) => (
                  <div
                    key={payment.id || index}
                    className="flex items-center justify-between p-3 bg-stone-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-stone-200 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-stone-600" />
                      </div>
                      <div>
                        <p className="font-medium text-stone-900 capitalize">
                          {payment.type?.replace("_", " ") || "Payment"}
                        </p>
                        <p className="text-sm text-stone-500">{formatDate(payment.date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-stone-900">{formatCurrency(payment.amount)}</p>
                      <Badge className={`text-xs ${getStatusBadge(payment.status)}`}>
                        {payment.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <DocumentsCard />
      </div>
    </Layout>
  );
};

export default EmployeeDashboard;
